import { useGetActiveOverlay } from 'features/explore/hooks/use-explore-overlay-poi';
import { useSetOverlayPOI } from 'features/explore/context/use-explore-ui-state-actions';
import React, { useMemo } from 'react';
import { ExploreDrawerOption } from 'features/explore/components/explore-side-bar/components/explore-drawer/explore-drawer-option/explore-drawer-option';
import { RegularOptionBody } from 'features/explore/components/explore-side-bar/components/explore-drawer/explore-drawer-option/option-body/regular-option-body';
import { POIActionProps } from 'features/explore/components/explore-side-bar/components/explore-drawer/drawer-options';
import { PlaceOverlay } from 'ui-components/google-map-layers/types/google-map-layers-types';
import { getPlaceOverlay } from 'ui-components/google-map-layers/utils/get-place-overlay';
import { isMarketEntity } from 'utils/market-poi/market-poi';
import { getFeatureFlags } from 'core/flow-control';

const getSubtitle = (poi: POIActionProps['poi']) => {
    const { accesibility_enable_explore_property_reports_ff } = getFeatureFlags();
    const isMarket = isMarketEntity(poi);

    if (poi.is_custom_poi) {
        return 'Visitation data is based on this Custom POI’s polygon';
    }

    if (accesibility_enable_explore_property_reports_ff) {
        return `An analysis of activity and demographics of a ${isMarket ? 'market' : 'property'}`;
    }

    return isMarket
        ? 'An analysis of activity and demographics of a market'
        : 'Visitation data is based on a verified building polygon';
};

export const VerifiedReportOption = ({ poi, enableSelection, value }: POIActionProps) => {
    const setOverlay = useSetOverlayPOI();
    const getActiveOverlay = useGetActiveOverlay();
    const { accesibility_enable_explore_property_reports_ff } = getFeatureFlags();

    const poiIsMarketEntity = isMarketEntity(poi);
    const overlayId = poiIsMarketEntity ? 'market' : 'poi';
    const overlayPoi: PlaceOverlay = useMemo(
        () => ({
            ...poi,
            overlayID: overlayId,
        }),
        [overlayId, poi],
    );

    const isActive = useMemo(() => {
        const overlay = getActiveOverlay(overlayPoi);
        if (!overlay) return false;
        return overlay.overlayID === overlayId && overlay.isActive;
    }, [getActiveOverlay, overlayId, overlayPoi]);

    const onActivate = () => {
        setOverlay({
            sourcePoi: poi.id,
            overlayPoi: getPlaceOverlay(overlayPoi, overlayId),
        });
    };

    return (
        <ExploreDrawerOption
            isActive={isActive}
            onActivate={onActivate}
            poi={poi}
            title={`Open a ${poiIsMarketEntity ? 'Market' : 'Property'} Report${
                accesibility_enable_explore_property_reports_ff ? '' : ' for ' + poi.name
            }`}
            subtitle={getSubtitle(poi)}
            testId={'verified-poi-option'}
            value={value}
        >
            <RegularOptionBody poi={overlayPoi} enableSelection={enableSelection} />
        </ExploreDrawerOption>
    );
};
